import React, { useEffect, useState, useMemo } from "react";
import { Offcanvas } from "react-bootstrap";
import { toast } from "react-toastify";
import { IoIosArrowBack } from "react-icons/io";
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useForm, Controller } from "react-hook-form";
import dayjs from "dayjs";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { BtnLoader } from "../../../../components/common/Loader";
import {
  useFetchInvoiceProjectsQuery,
  useCreateManualInvoiceMutation,
  useFetchInvoicesQueueQuery,
  useFetchInvoiceReferenceIdQuery,
} from "../../../../slice/logdetailSlice";
import { getBudgetTypeInfo, buildExistingProjectsMap } from "../../../../utils/budgetTypeUtils";
import { useTheme } from "../../../../ThemeContext";
import ProjectSelectAutocomplete from "../../../../components/common/ProjectSelectAutocomplete";

/**
 * Screen 1b: Manual Entry Offcanvas (Cash / Non-Zoho Offline Payment)
 * Connects with GET /invoices/projects and supports "Save & go to mapping" flow.
 */
const ManualEntryModal = ({ show, onHide, onSaveEntry, existingInvoices = [] }) => {
  const { isDarkMode } = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createManualInvoice, { isLoading: isCreatingInvoice }] = useCreateManualInvoiceMutation();

  // Fetch invoice projects from existing RTK Query endpoint
  const { data: projectsResponse } = useFetchInvoiceProjectsQuery();
  const projectList = useMemo(() => {
    const list = projectsResponse?.data || [];
    return [...list].sort((a, b) => {
      const titleA = a.title || a.project_name || a.name || "";
      const titleB = b.title || b.project_name || b.name || "";
      return titleA.localeCompare(titleB, undefined, { sensitivity: "base" });
    });
  }, [projectsResponse]);

  // Fetch fresh invoices queue when modal is shown to detect already added/mapped projects
  const { data: queueResponse } = useFetchInvoicesQueueQuery({}, { skip: !show });

  // Fetch dynamic manual invoice reference ID from GET /invoices/reference_id
  const {
    data: referenceResponse,
    isLoading: isReferenceLoading,
    refetch: refetchReferenceId,
  } = useFetchInvoiceReferenceIdQuery(undefined, {
    skip: !show,
  });

  const resolvedReferenceId = useMemo(() => {
    if (!referenceResponse) return "";
    const resData = referenceResponse?.data;
    if (typeof resData === "string") return resData.trim();
    if (typeof resData === "number") return `MAN-${resData}`;
    if (resData?.reference_number) return String(resData.reference_number).trim();
    if (resData?.invoice_number) return String(resData.invoice_number).trim();
    if (resData?.reference_id) return String(resData.reference_id).trim();
    if (resData?.reference) return String(resData.reference).trim();
    if (referenceResponse?.reference_number) return String(referenceResponse.reference_number).trim();
    if (referenceResponse?.invoice_number) return String(referenceResponse.invoice_number).trim();
    if (referenceResponse?.reference_id) return String(referenceResponse.reference_id).trim();
    if (referenceResponse?.reference) return String(referenceResponse.reference).trim();
    return "";
  }, [referenceResponse]);

  const resolvedSource = useMemo(() => {
    return referenceResponse?.data?.source || "MANUAL";
  }, [referenceResponse]);

  // Map of project IDs and project names that already have an invoice record in queue
  const existingProjectsMap = useMemo(() => {
    return buildExistingProjectsMap(
      existingInvoices,
      queueResponse?.data,
      projectList
    );
  }, [existingInvoices, queueResponse, projectList]);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      project: "",
      amount: "",
      dateReceived: dayjs(),
      paymentMode: "",
      source: "MANUAL — manual entry",
      reference: "MAN-000038 (auto)",
      reportingMonth: dayjs().subtract(1, "month"),
      description: "",
    },
  });

  const selectedProjectVal = watch("project");
  const selectedProjectObj = useMemo(() => {
    return projectList.find(
      (p) => String(p.project_id || p.id) === String(selectedProjectVal)
    );
  }, [projectList, selectedProjectVal]);

  const selectedBudgetInfo = useMemo(() => {
    return getBudgetTypeInfo(selectedProjectObj?.budget_type);
  }, [selectedProjectObj]);

  const isContractProject = selectedBudgetInfo.isContract;

  useEffect(() => {
    if (!isContractProject) {
      clearErrors("reportingMonth");
    }
  }, [isContractProject, clearErrors]);

  useEffect(() => {
    if (show) {
      setIsSubmitting(false);
      refetchReferenceId();
      const initialRef = resolvedReferenceId
        ? (resolvedReferenceId.includes("(auto)") ? resolvedReferenceId : `${resolvedReferenceId} (auto)`)
        : "MAN-000038 (auto)";
      reset({
        project: "",
        amount: "",
        dateReceived: dayjs(),
        paymentMode: "",
        source: "MANUAL — manual entry",
        reference: initialRef,
        reportingMonth: dayjs().subtract(1, "month"),
        description: "",
      });
    }
  }, [show, reset, refetchReferenceId, resolvedReferenceId]);

  useEffect(() => {
    if (resolvedReferenceId) {
      const formatted = resolvedReferenceId.includes("(auto)")
        ? resolvedReferenceId
        : `${resolvedReferenceId} (auto)`;
      setValue("reference", formatted);
    }
  }, [resolvedReferenceId, setValue]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const chosenProject = projectList.find(
        (p) => String(p.project_id || p.id) === String(data.project)
      );
      const projTitle =
        chosenProject?.title ||
        chosenProject?.project_name ||
        chosenProject?.name ||
        null;

      // Check if project has a valid budget type assigned
      const budgetInfo = getBudgetTypeInfo(chosenProject?.budget_type);
      if (!budgetInfo.isAssigned) {
        setError("project", {
          type: "manual",
          message: `Project "${projTitle || data.project}" has no Budget Type assigned. Please configure Budget Type (Fixed Cost, Contract, or Time & Material) in Project Details first.`,
        });
        toast.error(
          `Project "${projTitle || data.project}" does not have a Budget Type assigned in Project Details. Please set Budget Type before saving.`
        );
        setIsSubmitting(false);
        return;
      }

      const isContract = budgetInfo.isContract;
      const model = budgetInfo.model;
      const budgetType = chosenProject?.budget_type || budgetInfo.budgetType;
      const projId = Number(chosenProject?.project_id || chosenProject?.id || data.project);
      const effectiveClient =
        chosenProject?.client_name ||
        chosenProject?.client ||
        projTitle ||
        "—";

      const formattedReportingMonth = isContract && data.reportingMonth
        ? dayjs(data.reportingMonth).format("YYYY-MM")
        : undefined;

      // Duplicate prevention: for Contract projects, disallow duplicate invoices for the SAME reporting month
      const combinedInvoices = [
        ...(existingInvoices || []),
        ...(queueResponse?.data || []),
      ];

      if (isContract && formattedReportingMonth) {
        const isDuplicateMonth = combinedInvoices.some((inv) => {
          const invPid = Number(inv.project_id ?? inv.rawItem?.project_id);
          const invMonth = inv.reporting_month ?? inv.rawItem?.reporting_month;
          return invPid === projId && invMonth === formattedReportingMonth;
        });

        if (isDuplicateMonth) {
          setError("reportingMonth", {
            type: "manual",
            message: `Project "${projTitle || data.project}" already has an invoice for ${dayjs(data.reportingMonth).format("MMM YYYY")}. Please choose a different reporting month.`,
          });
          toast.error(
            `An invoice for "${projTitle || data.project}" in ${dayjs(data.reportingMonth).format("MMM YYYY")} already exists. Please choose a different reporting month.`
          );
          setIsSubmitting(false);
          return;
        }
      }

      const formattedDate = data.dateReceived
        ? dayjs(data.dateReceived).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD");

      const rawRef = resolvedReferenceId || data.reference?.replace(/\s*\(auto\)\s*/gi, "") || undefined;

      const payload = {
        project_id: projId,
        source: resolvedSource || "MANUAL",
        payment_date: formattedDate,
        total_amount: Number(data.amount),
        payment_mode: data.paymentMode || "Bank Transfer",
        scope_id: null,
        ...(rawRef ? { reference_number: rawRef, reference: rawRef, invoice_number: rawRef } : {}),
        ...(data.description ? { description: data.description } : {}),
        ...(isContract && formattedReportingMonth ? { reporting_month: formattedReportingMonth } : {}),
      };

      const response = await createManualInvoice(payload).unwrap();
      const createdData = response?.data || {};

      const createdInvoiceNumber = createdData.invoice_number || createdData.reference_number || `MANINV-${createdData.invoice_id || ""}`;
      const createdInvoiceId = createdData.invoice_id || createdData.id || Date.now();

      const newEntry = {
        id: createdInvoiceId,
        invoice_id: createdInvoiceId,
        source: createdData.source || "MANINV",
        invoice: createdInvoiceNumber,
        invoice_number: createdInvoiceNumber,
        date: formattedDate,
        payment_date: createdData.payment_date || formattedDate,
        invoiced_date: createdData.invoiced_date || formattedDate,
        client: createdData.client_name || effectiveClient,
        client_name: createdData.client_name || effectiveClient,
        project: projTitle,
        project_name: projTitle,
        project_id: projId,
        budget_type: createdData.budget_type || budgetType,
        model,
        reporting_month: createdData.reporting_month || formattedReportingMonth,
        amount: createdData.total_amount || data.amount,
        total_amount: createdData.total_amount || data.amount,
        status: createdData.status ? (createdData.status.charAt(0).toUpperCase() + createdData.status.slice(1).toLowerCase()) : "Mapped",
        action: "View",
        payment_mode: createdData.payment_mode || data.paymentMode || "—",
        paymentMode: createdData.payment_mode || data.paymentMode || "—",
        reference: createdData.reference_number || data.reference || createdInvoiceNumber,
        reference_number: createdData.reference_number || data.reference || createdInvoiceNumber,
        description: createdData.description || data.description || "—",
        created_by_name: createdData.created_by_name || "—",
        created_at: createdData.created_at || new Date().toISOString(),
        rawItem: {
          ...createdData,
          description: createdData.description || data.description,
          payment_mode: createdData.payment_mode || data.paymentMode,
          reference_number: createdData.reference_number || data.reference,
        },
      };

      if (onSaveEntry) {
        await onSaveEntry(newEntry);
      }
      toast.success(
        response?.message || `Manual invoice ${newEntry.invoice} created successfully! Opening ${isContract ? "allocation" : "mapping"}...`
      );
      onHide();
    } catch (error) {
      console.error("Failed to create manual invoice:", error);
      const serverErrors = error?.data?.errors;
      let errorMsg = error?.data?.message || error?.message || "Failed to save manual payment entry";
      if (serverErrors && typeof serverErrors === "object") {
        const firstErr = Object.values(serverErrors).flat()[0];
        if (firstErr) {
          errorMsg = `${errorMsg}: ${firstErr}`;
        }
      }
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={isSubmitting ? undefined : onHide}
      placement="end"
      className="custom-offcanvas"
      style={{ width: window.innerWidth <= 768 ? "90%" : "50%" }}
    >
      {/* Top Header matching Add Project form */}
      <div className="d-flex align-items-center justify-content-between filter-h-bg px-4 py-3">
        <button onClick={onHide} className="R_btn" type="button" disabled={isSubmitting}>
          <div className="d-flex text-rgba-gray align-items-center">
            <IoIosArrowBack className="me-1" />
            Manual Payment Entry
          </div>
        </button>
      </div>

      {/* Offcanvas Body: Form */}
      <Offcanvas.Body className="p-0">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            {/* Scrollable Form Body */}
            <div className="bg-white pt-3 px-4" style={{ flex: 1, overflowY: "auto" }}>

              {/* Row 1: Project (Full Width col-md-12) */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <label htmlFor="project" className="form-label">
                    Project <span className="text-rgbr-red">*</span>
                  </label>
                  <FormControl fullWidth>
                    <Controller
                      name="project"
                      control={control}
                      rules={{
                        required: "Project is required",
                      }}
                      render={({ field }) => (
                        <ProjectSelectAutocomplete
                          id="project"
                          projects={projectList}
                          value={field.value}
                          onChange={(newId) => field.onChange(newId)}
                          existingProjectsMap={existingProjectsMap}
                          error={Boolean(errors.project)}
                          isDarkMode={isDarkMode}
                        />
                      )}
                    />
                  </FormControl>
                  {errors.project && (
                    <span className="errorMsg">{errors.project.message}</span>
                  )}
                  {selectedProjectObj && !selectedBudgetInfo.isAssigned && !errors.project && (
                    <div
                      className="alert alert-warning py-1 px-2 small border-0 mt-2 mb-0 rounded-2"
                      style={{ fontSize: "12px" }}
                    >
                      ⚠️ This project does not have a Budget Type assigned in Project Details. Please configure Budget Type before saving.
                    </div>
                  )}
                </div>
              </div>

              {/* Row 2: Amount Received & Date Received */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="amount" className="form-label">
                    Amount Received <span className="text-rgbr-red">*</span>
                  </label>
                  <Controller
                    name="amount"
                    control={control}
                    rules={{
                      required: "Amount is required",
                      pattern: {
                        value: /^\d+(\.\d{1,2})?$/,
                        message: "Only numbers are allowed (e.g. 50000 or 50000.00)",
                      },
                      validate: {
                        positive: (val) => {
                          const num = Number(val);
                          if (isNaN(num) || num <= 0) {
                            return "Amount must be greater than 0";
                          }
                          return true;
                        },
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="amount"
                        placeholder="Enter amount"
                        fullWidth
                        variant="outlined"
                        error={Boolean(errors.amount)}
                        inputProps={{
                          inputMode: "decimal",
                          autoComplete: "off",
                        }}
                        onKeyDown={(e) => {
                          // Allow navigation and editing shortcuts
                          if (
                            [
                              "Backspace",
                              "Tab",
                              "ArrowLeft",
                              "ArrowRight",
                              "Delete",
                              "Enter",
                              "Home",
                              "End",
                            ].includes(e.key) ||
                            ((e.ctrlKey || e.metaKey) &&
                              ["a", "c", "v", "x", "z"].includes(e.key.toLowerCase()))
                          ) {
                            return;
                          }
                          // Allow single decimal point
                          if (e.key === ".") {
                            if ((field.value || "").toString().includes(".")) {
                              e.preventDefault();
                            }
                            return;
                          }
                          // Block alphabets, symbols, spaces and special characters
                          if (!/^[0-9]$/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          let rawVal = e.target.value.replace(/[^0-9.]/g, "");
                          if (rawVal.startsWith(".")) {
                            rawVal = "0" + rawVal;
                          }
                          const parts = rawVal.split(".");
                          if (parts.length > 2) {
                            rawVal = parts[0] + "." + parts.slice(1).join("");
                          }
                          if (rawVal.includes(".")) {
                            const [intPart, decPart] = rawVal.split(".");
                            rawVal = `${intPart}.${decPart.slice(0, 2)}`;
                          }
                          field.onChange(rawVal);
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pasted = (e.clipboardData || window.clipboardData)?.getData("text") || "";
                          let cleaned = pasted.replace(/[^0-9.]/g, "");
                          if (cleaned.startsWith(".")) {
                            cleaned = "0" + cleaned;
                          }
                          const parts = cleaned.split(".");
                          if (parts.length > 2) {
                            cleaned = parts[0] + "." + parts.slice(1).join("");
                          }
                          if (cleaned.includes(".")) {
                            const [intPart, decPart] = cleaned.split(".");
                            cleaned = `${intPart}.${decPart.slice(0, 2)}`;
                          }
                          if (cleaned) {
                            field.onChange(cleaned);
                          }
                        }}
                      />
                    )}
                  />
                  {errors.amount && (
                    <span className="errorMsg">{errors.amount.message}</span>
                  )}
                </div>

                <div className="col-md-6">
                  <label htmlFor="dateReceived" className="form-label">
                    Date Received <span className="text-rgbr-red">*</span>
                  </label>
                  <div className="d-flex flex-column w-100">
                    <Controller
                      name="dateReceived"
                      control={control}
                      rules={{
                        required: "Date received is required",
                        validate: (date) => {
                          if (!date || !dayjs(date).isValid()) {
                            return "Please enter a valid date";
                          }
                          if (dayjs(date).year() < 1990) {
                            return "Date cannot be earlier than 1990";
                          }
                          return true;
                        },
                      }}

                      render={({ field }) => (
                        <DatePicker
                          value={field.value || null}
                          format="DD/MM/YYYY"
                          minDate={dayjs("1990-01-01")}
                          onChange={(date) => {
                            field.onChange(date);
                            if (date && dayjs(date).isValid()) {
                              if (dayjs(date).year() < 1990) {
                                setError("dateReceived", {
                                  type: "manual",
                                  message: "Date cannot be earlier than 1990",
                                });
                              } else {
                                clearErrors("dateReceived");
                              }
                            }
                          }}

                          onError={(newError) => {
                            if (newError === "minDate") {
                              setError("dateReceived", {
                                type: "manual",
                                message: "Date cannot be earlier than 1990",
                              });
                            } else if (newError === "invalidDate") {
                              setError("dateReceived", {
                                type: "manual",
                                message: "Please enter a valid date",
                              });
                            } else if (!newError && field.value && dayjs(field.value).isValid() && dayjs(field.value).year() >= 1990) {
                              clearErrors("dateReceived");
                            }
                          }}
                          
                          slotProps={{
                            popper: {
                              sx: { zIndex: 1600 },
                            },
                            textField: {
                              fullWidth: true,
                              placeholder: "DD/MM/YYYY",
                              error: Boolean(errors.dateReceived),
                            },
                          }}
                        />
                      )}
                    />
                    {errors.dateReceived && (
                      <span className="errorMsg">{errors.dateReceived.message}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 3: Payment Mode & Source */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="paymentMode" className="form-label">
                    Payment Mode <span className="text-rgbr-red">*</span>
                  </label>
                  <FormControl fullWidth>
                    <Controller
                      name="paymentMode"
                      control={control}
                      rules={{ required: "Payment mode is required" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          id="paymentMode"
                          IconComponent={ExpandMore}
                          displayEmpty
                          MenuProps={{
                            sx: { zIndex: 1600 },
                            PaperProps: {
                              sx: { zIndex: 1600 },
                            },
                          }}
                          renderValue={(selected) =>
                            selected ? (
                              selected
                            ) : (
                              <span className="lightText">Select payment mode</span>
                            )
                          }
                        >
                          <MenuItem value="Cash">Cash</MenuItem>
                          <MenuItem value="Cheque">Cheque</MenuItem>
                          <MenuItem value="Direct bank transfer">
                            Direct bank transfer
                          </MenuItem>
                          <MenuItem value="UPI">UPI</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
                  {errors.paymentMode && (
                    <span className="errorMsg">{errors.paymentMode.message}</span>
                  )}
                </div>

                <div className="col-md-6">
                  <label htmlFor="source" className="form-label">
                    Source
                  </label>
                  <TextField
                    id="source"
                    fullWidth
                    value="MANUAL — manual entry"
                    disabled
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: isDarkMode ? "#94a3b8" : "#6c757d",
                      },
                      backgroundColor: isDarkMode ? "#1e293b" : "#f8f9fa",
                    }}
                  />
                </div>
              </div>

              {/* Row 4: Reference & Reporting Month (shown when Contract Project is selected) */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="reference" className="form-label">
                    Reference
                  </label>
                  <TextField
                    id="reference"
                    fullWidth
                    value={
                      isReferenceLoading
                        ? "Loading reference..."
                        : watch("reference") || (resolvedReferenceId ? `${resolvedReferenceId} (auto)` : "MAN-000038 (auto)")
                    }
                    disabled
                    variant="outlined"
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: isDarkMode ? "#94a3b8" : "#6c757d",
                      },
                      backgroundColor: isDarkMode ? "#1e293b" : "#f8f9fa",
                    }}
                  />
                </div>

                {isContractProject && (
                  <div className="col-md-6">
                    <label htmlFor="reportingMonth" className="form-label">
                      Reporting Month <span className="text-rgbr-red">*</span>
                    </label>
                    <div className="d-flex flex-column w-100">
                      <Controller
                        name="reportingMonth"
                        control={control}
                        rules={
                          isContractProject
                            ? {
                                required: "Reporting month is required",
                                validate: (val) => {
                                  if (!val || !dayjs(val).isValid()) {
                                    return "Please select a valid month";
                                  }
                                  return true;
                                },
                              }
                            : {}
                        }
                        render={({ field }) => (
                          <DatePicker
                            value={field.value || null}
                            views={["year", "month"]}
                            format="MM/YYYY"
                            onChange={(date) => field.onChange(date)}
                            slotProps={{
                              popper: {
                                sx: { zIndex: 1600 },
                              },
                              textField: {
                                fullWidth: true,
                                placeholder: "MM/YYYY",
                                error: Boolean(errors.reportingMonth),
                              },
                            }}
                          />
                        )}
                      />
                      {errors.reportingMonth && (
                        <span className="errorMsg">{errors.reportingMonth.message}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Row 5: Description (ReactQuill with validation) */}
              <div className="row mb-3">
                <div className="col-md-12">
                  <label htmlFor="description" className="form-label">
                    Description <span className="text-rgbr-red">*</span>
                  </label>
                  <Controller
                    name="description"
                    control={control}
                    rules={{
                      required: "Description is required",
                      validate: (value) => {
                        const stripped = value ? value.replace(/<[^>]*>/g, "").trim() : "";
                        return stripped.length > 0 || "Description is required";
                      },
                    }}
                    render={({ field }) => (
                      <ReactQuill
                        {...field}
                        theme="snow"
                        placeholder="Enter description"
                        modules={{
                          toolbar: [
                            [{ font: [] }],
                            [{ size: [] }],
                            ["bold", "italic", "underline"],
                            [{ color: [] }, { background: [] }],
                            [{ align: [] }],
                          ],
                        }}
                        style={{ borderRadius: 4 }}
                      />
                    )}
                  />
                  {errors.description && (
                    <span className="errorMsg">{errors.description.message}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Actions matching Add Project form */}
            <div className="bg-white px-4 py-3">
              <div className="d-flex justify-content-start">
                <button
                  type="submit"
                  className="position-relative add_btn me-3"
                  disabled={isSubmitting || isCreatingInvoice}
                  style={{ minWidth: "190px" }}
                >
                  {(isSubmitting || isCreatingInvoice) ? (
                    <div className="d-flex align-items-center gap-2">
                      <BtnLoader />
                      <span>
                        {isContractProject
                          ? "Saving & Going to Allocation..."
                          : "Saving & Going to Mapping..."}
                      </span>
                    </div>
                  ) : isContractProject ? (
                    "Save & go to allocation"
                  ) : (
                    "Save & go to mapping"
                  )}
                </button>
                <button
                  type="button"
                  className="cancel_btn"
                  onClick={onHide}
                  disabled={isSubmitting || isCreatingInvoice}
                  style={(isSubmitting || isCreatingInvoice) ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </LocalizationProvider>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default ManualEntryModal;
